import React, { useRef, useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import AlertManager from "../../shared/Alert/Alert";
import LandedCostSkuExclusionTable from "../../shared/LandedCostSkuExclusionTable/LandedCostSkuExclusionTable";
import {
  ACCEPTED_MIME_TYPES,
  FILE_ACCEPTED_TYPES,
  MAX_FILE_SIZE_MB,
} from "../LandedCostUpload/LandedCostUploadConstants";
import { downloadTemplateFile } from "../../API/landedCostVendorDownloadTemplateFileApi";
import { uploadLandedCostFile } from "../../API/landedCostVendorUploadApi";
import classes from "../LandedCostUpload/LandedCostUpload.module.css";
import { useLandedCostSkuExclusionHistory } from "./LandedCostSkuExclusion.hooks";

const SUCCESS_MESSAGE = "File received. You will get an email after processing.";

const LandedCostSkuExclusion = () => {
  const fileInputRef = useRef(null);
  const { rows, loading, fetchHistory } = useLandedCostSkuExclusionHistory();

  const [selectedFileName, setSelectedFileName] = useState("");

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setSelectedFileName("");
  };

  const handleFileChange = (event) => {
    const file = event?.target?.files?.[0];
    setSelectedFileName(file ? file.name : "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const file = fileInputRef.current?.files?.[0];

    if (!file) {
      AlertManager.error("Choose a spreadsheet to upload.", { contactSupport: false });
      resetFileInput();
      return;
    }

    const isExcel = ACCEPTED_MIME_TYPES.includes(file.type) || /\.xls[x]?$/i.test(file.name);
    if (!isExcel) {
      AlertManager.error("Only .xlsx or .xls files are supported.", { contactSupport: false });
      resetFileInput();
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      AlertManager.error(`File size exceeds ${MAX_FILE_SIZE_MB} MB.`, { contactSupport: false });
      resetFileInput();
      return;
    }

    let successShown = false;
    const showSuccess = () => {
      if (!successShown) {
        successShown = true;
        AlertManager.success(SUCCESS_MESSAGE, { contactSupport: false });
      }
    };
    const successTimeoutId = setTimeout(showSuccess, 1000);

    try {
      const response = await uploadLandedCostFile({ file, uploadType: "SKUEXCLUSION" });
      if (!response) {
        clearTimeout(successTimeoutId);
        return;
      }

      if (response.status === 200 || response.status === 201) {
        clearTimeout(successTimeoutId);
        showSuccess();
        fetchHistory?.();
        event.target.reset();
        setSelectedFileName("");
        return;
      }

      clearTimeout(successTimeoutId);
      const payload = await response.json().catch(() => ({}));
      AlertManager.error(payload?.message || "There were issues while uploading the file.");
      resetFileInput();
    } catch (error) {
      clearTimeout(successTimeoutId);
      AlertManager.error("There were issues while uploading the file.");
      resetFileInput();
    }
  };

  const handleTemplateDownload = async () => {
    try {
      await downloadTemplateFile("SKUEXCLUSION");
    } catch (error) {
      AlertManager.error("Unable to download the SKU exclusion template.", {
        contactSupport: false,
      });
    }
  };

  return (
    <section className={classes.body}>
      <header className={classes.heading}>Upload SKU exclusion from tariff for landed cost</header>

      <form className={classes.container} onSubmit={handleSubmit}>
        <Box className={classes.fieldRow}>
          <Button component="label" variant="outlined" size="small">
            Choose File
            <input
              hidden
              ref={fileInputRef}
              type="file"
              accept={FILE_ACCEPTED_TYPES}
              data-testid="sku-exclusion-file-input"
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" className={classes.fileNameDisplay}>
            {selectedFileName || "No file selected"}
          </Typography>
          <Typography variant="body2" className={classes.helperText}>
            Accepted formats: .xlsx / .xls Max size {MAX_FILE_SIZE_MB} MB
          </Typography>
        </Box>

        <Box className={classes.actions}>
          <Button className={classes.uploadBtn} type="submit" data-testid="sku-exclusion-upload-btn">
            Upload
          </Button>
          <Tooltip title="Download the standardized SKU exclusion template">
            <Button className={classes.uploadBtn} variant="text" onClick={handleTemplateDownload}>
              Download Template
            </Button>
          </Tooltip>
        </Box>
      </form>

      <div className={classes.hLine} />

      <div className={classes.fileuploadHistory}>
        <LandedCostSkuExclusionTable rows={rows} loading={loading} />
      </div>
    </section>
  );
};

export default LandedCostSkuExclusion;
